import ReferencePathService from '~/services/referencePathServices/referencePathService'
import {
  IReferencePathNodeObjectModel,
  IReferencePathNodeObjectModelRelation,
  IReferencePathNodeSpecific, IReferencePathReferenceDetails, ReferencePathReferenceDetailsType
} from '~/interfaces/referencePathInterfaces'
import ObjectModelRelation from '~/models/objectModelRelations/objectModelRelation'
import ObjectModelRelationDefinition from '~/models/objectModelRelations/objectModelRelationDefinition'
import { ReferenceNodeType } from '~/enums/referenceNodeType'
import { GenericType } from '~/interfaces/genericTypeInterfaces'
import { ModelType } from '~/enums/modelType'
import { ObjectModelRelationType } from '~/enums/objectModelRelationType'
import {PermitteeType} from "~/enums/permitteeType";

export default class ObjectModelRelationPathService extends ReferencePathService {
  get imposterModelType(): ModelType {
    return this.referenceModelType
  }

  constructor(
    private relationNode: IReferencePathNodeObjectModelRelation,
    private objectModelNode: IReferencePathNodeObjectModel
  ) {
    super();
  }

  get label(): string {
    const relation = this.relationNode?.reference();
    if (!relation) return null

    if (relation.relationType === ObjectModelRelationType.Account || relation.relationType === ObjectModelRelationType.EntityAccount) {
      return relation.label_title
    }

    const objectModel = this.objectModelNode?.reference()
    if (!objectModel) return null

    const inverse = this.relationNode.inverse; // from perspective of preceding OM in traversal, not objectModelNode's OM

    // ex. inverse === false => User "Relates to many User as Following(inverse label title)"
    //     inverse === true  => User "Relates to many User as Follower(label title)"
    return `Relates to ${ObjectModelRelationDefinition.getAmountName(relation.relationTypeFrom(inverse))} ${objectModel.titleOrDefault} as ${relation.labelTitleOrDefaultFor(!inverse)}`
  }

  get nodes(): Array<IReferencePathNodeObjectModelRelation | IReferencePathNodeObjectModel> {
    if (this.isAccount) return [this.relationNode];
    return [this.relationNode, this.objectModelNode];
  }

  reference(): ObjectModelRelation {
    return ObjectModelRelation.repo.findById(this.relationNode.reference_id);
  }

  buildReference(attrs: Record<string, any>): ObjectModelRelation {
    if (attrs.id !== this.relationNode.reference_id) return

    ObjectModelRelation.repo.findOrCreate(attrs.id).loadFromJson(attrs)
    return this.reference()
  }

  get referenceGenericModelType(): GenericType {
    return this.reference()?.genericType;
  }

  get isAccount(): boolean {
    if (!this.relationNode) return false;
    if (!this.relationNode.reference()) return false;
    return this.relationNode.reference().isAccountRelationship
  }

  get referenceModelType(): ModelType {
    return this.isAccount ? ModelType.User : this.referenceGenericModelType
  }

  canLinkTo(node: IReferencePathNodeSpecific): boolean {
    if (!node && this.isAccount) return true;
    if (node && this.isAccount && node.reference_type === ReferenceNodeType.AccountRelationMetadata) return true;
    let decision: boolean
    const relation = this.reference()
    if (relation) {
      switch (node.reference_type) {
        case ReferenceNodeType.ObjectModel:
          if (this.relationNode.inverse) {
            decision = relation.object_model_id === node.reference_id
          } else {
            decision = relation.related_object_model_ids.includes(node.reference_id)
          }
          break
        default:
          decision = false
          break;
      }
    }
    this.debugCanLinkTo('ObjectModelRelationPathService', this.relationNode, node, decision)
    return decision
  }

  get referenceDetails(): IReferencePathReferenceDetails {
    if (this.isAccount) {
      return {type: ReferencePathReferenceDetailsType.User};
    }

    return null;
  }

  get referenceIsCollection(): boolean {
    if (this.isAccount) return false;
    return this.reference()?.relatesToManyFrom(this.nodes[0].inverse)
  }

  get referencePermitteeType(): PermitteeType {
    if (this.isAccount) {
      return PermitteeType.accountRelation;
    }
  }

}